/** Configuration management for the application. */

// Never throw errors during module evaluation (build time)
// Validate at runtime when values are actually used
function getEnvVar(name: string, _required = true): string {
  const value = process.env[name];
  // Always return empty string if not set - never throw during module load
  // Runtime validation happens when the value is actually used
  return value || '';
}

// Runtime validation helper - call this when you need to ensure a var is set
export function requireEnvVar(name: string): string {
  const value = process.env[name];
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

export const config = {
  // Google OAuth Configuration
  // Support both GOOGLE_CLIENT_ID and GOOGLE_OAUTH_CLIENT_ID for compatibility
  googleClientId: getEnvVar('GOOGLE_CLIENT_ID', false) || getEnvVar('GOOGLE_OAUTH_CLIENT_ID'),
  googleClientSecret: getEnvVar('GOOGLE_CLIENT_SECRET', false) || getEnvVar('GOOGLE_OAUTH_SECRET'),
  googleRedirectUri: getEnvVar('GOOGLE_REDIRECT_URI'),
  gmailScopes:
    getEnvVar('GMAIL_SCOPES', false) ||
    'https://www.googleapis.com/auth/gmail.modify,https://www.googleapis.com/auth/gmail.labels',

  // Supabase Configuration
  supabaseUrl: getEnvVar('NEXT_PUBLIC_SUPABASE_URL', false) || getEnvVar('SUPABASE_URL'),
  supabaseServiceRoleKey: getEnvVar('SUPABASE_SERVICE_ROLE_KEY', false),
  supabaseAnonKey:
    getEnvVar('NEXT_PUBLIC_SUPABASE_ANON_KEY', false) || getEnvVar('SUPABASE_ANON_KEY'),
  supabaseDbUrl: getEnvVar('SUPABASE_DB_URL', false),
  supabasePassword: getEnvVar('SUPABASE_PASSWORD', false),

  // Stripe Configuration (server-side only - no client-side Stripe.js)
  stripeSecretKey: getEnvVar('STRIPE_SECRET_KEY', false),
  stripeWebhookSecret: getEnvVar('STRIPE_WEBHOOK_SECRET', false),

  // Sentry Configuration
  sentryDsn: getEnvVar('SENTRY_DSN', false) || getEnvVar('NEXT_PUBLIC_SENTRY_DSN', false),

  // Crisp Chat Configuration
  crispWebsiteId:
    getEnvVar('NEXT_PUBLIC_CRISP_WEBSITE_ID', false) || 'c0bbba85-fcc3-4029-b529-f2cc9176b7ba',
  // Server-only secret (Crisp's "Identity Verification Key", from Crisp's website
  // settings) used to HMAC-sign the `user:hash` value sent to Crisp so it can
  // cryptographically verify identity claims instead of trusting an unsigned user
  // ID. Never exposed to the client — see lib/crisp-identity.ts.
  crispIdentityKey: getEnvVar('CRISP_IDENTITY_KEY', false),

  // Encryption & Security
  encryptionKey: getEnvVar('ENCRYPTION_KEY', false) || 'placeholder-key-32-bytes-long!!',
  // Shared secret authenticating internal service-to-service API calls (e.g. the
  // OAuth callback and Stripe webhook triggering a historical reprocess).
  internalApiSecret: getEnvVar('INTERNAL_API_SECRET', false),
  jwtSecretKey: getEnvVar('JWT_SECRET_KEY', false),
  jwtAlgorithm: getEnvVar('JWT_ALGORITHM', false) || 'HS256',
  jwtAccessTokenExpireMinutes: parseInt(
    getEnvVar('JWT_ACCESS_TOKEN_EXPIRE_MINUTES', false) || '30',
    10
  ),

  // Application Configuration
  appUrl: (() => {
    const envUrl = getEnvVar('APP_URL', false) || getEnvVar('NEXT_PUBLIC_APP_URL', false);
    if (envUrl && !envUrl.includes('localhost')) return envUrl;

    // In Vercel, use VERCEL_URL if available (doesn't include protocol)
    const vercelUrl = getEnvVar('VERCEL_URL', false);
    if (vercelUrl && !vercelUrl.includes('localhost')) {
      // VERCEL_URL doesn't include protocol, so add https
      return `https://${vercelUrl}`;
    }

    // In production (Vercel), always use production URL
    // Check for Vercel environment indicators
    if (
      process.env.VERCEL ||
      process.env.VERCEL_ENV === 'production' ||
      (typeof process !== 'undefined' &&
        process.env.NODE_ENV === 'production' &&
        !process.env.VERCEL_URL?.includes('localhost'))
    ) {
      return 'https://emailferret.io';
    }

    // Default to localhost for local development
    return 'http://localhost:3000';
  })(),
  labelName: 'Ignore',
  defaultThreshold: parseInt(getEnvVar('DEFAULT_THRESHOLD', false) || '3', 10), // Default threshold for new users
  environment: getEnvVar('ENVIRONMENT', false) || getEnvVar('NODE_ENV', false) || 'development',

  // Rate Limiting
  rateLimitPerMinute: parseInt(getEnvVar('RATE_LIMIT_PER_MINUTE', false) || '60', 10),
  rateLimitPerHour: parseInt(getEnvVar('RATE_LIMIT_PER_HOUR', false) || '1000', 10),

  // Stricter, endpoint-specific rate limits for higher-abuse-risk operations:
  // unauthenticated auth entry points (brute force / account enumeration risk) and
  // expensive operations (background job triggers, LLM calls) that are far cheaper
  // to abuse than they are to serve.
  rateLimitAuthSignupPerMinute: parseInt(
    getEnvVar('RATE_LIMIT_AUTH_SIGNUP_PER_MINUTE', false) || '10',
    10
  ),
  rateLimitAuthSignupPerHour: parseInt(
    getEnvVar('RATE_LIMIT_AUTH_SIGNUP_PER_HOUR', false) || '30',
    10
  ),
  rateLimitSessionExchangePerMinute: parseInt(
    getEnvVar('RATE_LIMIT_SESSION_EXCHANGE_PER_MINUTE', false) || '10',
    10
  ),
  rateLimitSessionExchangePerHour: parseInt(
    getEnvVar('RATE_LIMIT_SESSION_EXCHANGE_PER_HOUR', false) || '30',
    10
  ),
  rateLimitJobStartPerMinute: parseInt(
    getEnvVar('RATE_LIMIT_JOB_START_PER_MINUTE', false) || '5',
    10
  ),
  rateLimitJobStartPerHour: parseInt(getEnvVar('RATE_LIMIT_JOB_START_PER_HOUR', false) || '15', 10),
  rateLimitProfileEvaluatePerMinute: parseInt(
    getEnvVar('RATE_LIMIT_PROFILE_EVALUATE_PER_MINUTE', false) || '5',
    10
  ),
  rateLimitProfileEvaluatePerHour: parseInt(
    getEnvVar('RATE_LIMIT_PROFILE_EVALUATE_PER_HOUR', false) || '20',
    10
  ),

  // Logging
  logLevel: getEnvVar('LOG_LEVEL', false) || 'INFO',

  // AI Gateway Configuration (for LLM-based domain trust assessment)
  // Uses Vercel AI SDK which automatically routes to AI Gateway when vck_ key is detected
  // API key format: vck_... (Vercel AI Gateway key)
  // The SDK handles routing automatically - no URL configuration needed
  // Supports both OpenAI models (gpt-*) and Google models (gemini-*)
  aiGatewayApiKey: getEnvVar('AI_GATEWAY_API_KEY', false),
  aiGatewayModel: getEnvVar('AI_GATEWAY_MODEL', false) || 'gemini-2.5-flash-lite',
  // Used when the primary model returns GatewayInternalServerError ("high demand") — a
  // different provider than the default so a provider-wide outage doesn't take out both.
  aiGatewayFallbackModel: getEnvVar('AI_GATEWAY_FALLBACK_MODEL', false) || 'gpt-4o-mini',

  // Helper methods
  get gmailScopesList(): string[] {
    return this.gmailScopes.split(',').map((s) => s.trim());
  },

  get isProduction(): boolean {
    return this.environment.toLowerCase() === 'production';
  },

  validateEncryptionKey(): void {
    if (Buffer.from(this.encryptionKey, 'utf-8').length !== 32) {
      throw new Error('ENCRYPTION_KEY must be exactly 32 bytes');
    }
  },
};

// Don't validate on import - validate at runtime when encryption is actually used
